import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge"
import type { TemplateModule } from "shared"
import type { TemplateEvent } from "../stores/templateState"
import type { DragData } from "../../../dragAndDrop/dragData"
import { dragKind } from "../../../dragAndDrop/dragKind"
import { dropZone } from "../../../dragAndDrop/dropZone"
import { offCanvas } from "../../../dragAndDrop/offCanvas"
import { moveForDrop, placeForDrop } from "../../../dragAndDrop/reorder"

/** A module on the canvas, by its id, as it is dragged. */
export const placedModule = dragKind<string>("placed module")

/** A module in the library, whole, as it is dragged onto the canvas. */
export const libraryModule = dragKind<TemplateModule>("library module")

/** A card on the canvas, by its module's id, as a place to drop on. */
export const placedModuleZone = dropZone<string>("placed module")

/** The canvas, which takes a module from the library. */
export const templateCanvas = dropZone("template canvas")

/** Whether a drag is a placed module, which the page takes off the template. */
export const isPlacedModule = (source: DragData) =>
  placedModule.from(source) !== undefined

/** Whether a drag is a module from the library, which the canvas adds. */
export const isLibraryModule = (source: DragData) =>
  libraryModule.from(source) !== undefined

/** Whether a drag is a module, placed or from the library: what the canvas and its cards take. */
export const isModule = (source: DragData) =>
  isPlacedModule(source) || isLibraryModule(source)

/**
 * The event a drop makes on the template, as its buttons would make it, or
 * null for a drop no rule covers. Each rule names both what was dragged and
 * where it landed, so a place that starts taking a new kind of drag changes
 * nothing until it has a rule here.
 */
export function eventForDrop(
  modules: TemplateModule[],
  source: DragData,
  target: DragData | undefined,
): TemplateEvent | null {
  if (!target) return null
  const added = libraryModule.from(source)
  if (added) {
    if (templateCanvas.is(target)) {
      return { type: "added", module: added, at: modules.length }
    }
    const onto = placedModuleZone.from(target)
    return onto === undefined
      ? null
      : {
          type: "added",
          module: added,
          at: placeForDrop(modules, onto, extractClosestEdge(target)),
        }
  }
  const id = placedModule.from(source)
  if (id === undefined) return null
  if (offCanvas.is(target)) return { type: "removed", id }
  const over = placedModuleZone.from(target)
  if (over === undefined) return null
  const move = moveForDrop(modules, id, over, extractClosestEdge(target))
  return move && { type: "moved", ...move }
}
